/**
 * Root providers.
 *
 * Wires: QueryClientProvider (TanStack Query), Toaster (sonner), and
 * ErrorBoundary. On mount, hydrates the HTTP client's Bearer-token slot
 * from the persisted auth store so every subsequent request is
 * automatically authenticated.
 */
import { QueryClient, QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { Toaster, toast } from "sonner";

import { AppErrorBoundary } from "@/app/error-boundary";
import { useAuthStore } from "@/entities/auth/store";
import { me as fetchMe } from "@/entities/auth";
import { setAuthToken } from "@/shared/api/http";
import { useWorkspaceStore } from "@/app/store/workspace-store";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (failureCount, error) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const status = (error as any)?.status ?? 0;
        if (status >= 400 && status < 500) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
    },
  },
});

interface ProvidersProps {
  children: ReactNode;
}

function AuthHydrator(): null {
  const token = useAuthStore((s) => s.accessToken);
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const setMe = useAuthStore((s) => s.setMe);
  const qc = useQueryClient();
  const previousUserRef = useRef<string | null | undefined>(undefined);

  // Patch 19 · P0 stability. When the authenticated user identity changes
  // (login as a different user, logout, or token revocation), the
  // workspace store may still hold a currentWorkspaceId from the previous
  // session. If any component reads it before useBootstrapWorkspace
  // reconciles, the backend responds "Not a member of this workspace".
  // We therefore clear the workspace-store selection AND flush any cached
  // TanStack queries whenever the user id transitions.
  useEffect(() => {
    if (previousUserRef.current === undefined) {
      previousUserRef.current = userId;
      return;
    }
    if (previousUserRef.current !== userId) {
      const ws = useWorkspaceStore.getState();
      ws.setWorkspaces([]);
      ws.selectWorkspace(null);
      ws.setMe(null);
      qc.clear();
      previousUserRef.current = userId;
    }
  }, [userId, qc]);

  useEffect(() => {
    setAuthToken(token);
    if (token) {
      fetchMe().then(setMe).catch(async (err) => {
        // Patch 24 + Patch 34.2.2g · session refresh.
        // If the server rejects the persisted token (401/404), FIRST
        // try to silently refresh the access token from the HttpOnly
        // refresh cookie. Only clear the auth store when the refresh
        // itself fails (expired refresh, revoked session, network).
        const status = (err as { status?: number })?.status ?? 0;
        if (status === 401 || status === 404) {
          const { attemptSilentRefresh } = await import(
            "@/entities/auth/silentRefresh"
          );
          const refreshed = await attemptSilentRefresh();
          if (refreshed) {
            // Refresh worked — replay the fetchMe. If it 401s again
            // we treat that as a genuinely dead session.
            try {
              const me = await fetchMe();
              setMe(me);
              return;
            } catch { /* fall through to clear */ }
          }
          useAuthStore.getState().clear();
          useWorkspaceStore.getState().setWorkspaces([]);
          useWorkspaceStore.getState().selectWorkspace(null);
          useWorkspaceStore.getState().setMe(null);
          toast.error("Your session expired. Please sign in again.", {
            id: "session-expired",
            duration: 5000,
          });
        }
      });
    }
  }, [token, setMe]);
  return null;
}

/**
 * ResponsiveToaster — Milestone 37.3 fix.
 *
 * On mobile viewports (< md), Focus Flow renders the MissionShell as a
 * full-screen overlay with a sticky bottom footer that owns the primary
 * CTA. A Sonner toast anchored to the bottom-right therefore overlaps
 * (and intercepts pointer events on) the Approve / next-step buttons —
 * a genuine UX defect verified on Pixel 7. Anchor toasts to the top on
 * narrow viewports so success/error banners never occlude the decision
 * surface. Desktop keeps the standard bottom-right stacking.
 */
function ResponsiveToaster(): JSX.Element {
  const [isMobile, setIsMobile] = useState<boolean>(() =>
    typeof window !== "undefined"
      && window.matchMedia("(max-width: 767px)").matches,
  );
  useEffect(() => {
    const mql = window.matchMedia("(max-width: 767px)");
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);
  return (
    <Toaster
      richColors
      position={isMobile ? "top-center" : "bottom-right"}
    />
  );
}

export function AppProviders({ children }: ProvidersProps): JSX.Element {
  return (
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthHydrator />
        {children}
        <ResponsiveToaster />
      </QueryClientProvider>
    </AppErrorBoundary>
  );
}
